import { TourItinerary } from '../../models/tour-itinerary';
import { TourService } from '../../tour.service';
import { Component, computed, inject } from '@angular/core';

/**
 * Component for displaying tour progress and allowing navigation between itineraries.
 *
 * The TourProgressComponent provides a UI card that shows the current tour progress,
 * lists available itineraries, and allows users to resume skipped itineraries.
 * It appears as a floating card when the tour is active and the showTourProgressCard
 * signal is set to true.
 *
 * The component automatically filters itineraries to only show those that have
 * showNameInTourProgress set to true, giving tour designers control over which
 * itineraries appear in the progress UI.
 *
 * @example
 * ```html
 * <!-- Include in your app template -->
 * <mop-tour-progress></mop-tour-progress>
 *
 * <!-- The component will automatically show/hide based on tour state -->
 * ```
 *
 * @example
 * ```typescript
 * // Control visibility in your tour configuration
 * const tourStop = new TourStopPoi('#element', 'Title', 'Description', {
 *   nextButton: {
 *     displayText: 'Continue',
 *     listener: () => {
 *       tourService.showTourProgressCard.set(true); // Show progress card
 *       tourService.nextStop();
 *     }
 *   }
 * });
 * ```
 */
@Component({
  selector: 'mop-tour-progress',
  templateUrl: './tour-progress.component.html',
  styleUrl: './tour-progress.component.scss',
  host: {
    class: 'card shadow-lg',
  },
})
export class TourProgressComponent {
  /** The tour service instance for managing tour state */
  private readonly tourService = inject(TourService);

  /**
   * Computed signal that determines if the progress card should be visible.
   *
   * The card is shown when both the tour is started and the showTourProgressCard
   * signal is set to true. This allows for fine-grained control over when the
   * progress card appears during the tour.
   *
   * @returns True if the progress card should be displayed, false otherwise
   */
  show = computed(
    () =>
      this.tourService.tourStarted() && this.tourService.showTourProgressCard()
  );

  /**
   * Computed signal containing itineraries that should be displayed in the progress UI.
   *
   * This filters the complete list of itineraries to only include those that have
   * showNameInTourProgress set to true. This allows tour designers to control which
   * itineraries appear in the progress card while keeping others hidden.
   *
   * @returns Array of itineraries to display in the progress UI
   */
  itineraries = computed(() =>
    this.tourService
      .itineraries()
      .filter((itinerary) => itinerary.showNameInTourProgress)
  );

  /**
   * Resumes a previously skipped itinerary.
   *
   * This method finds the index of the specified itinerary in the complete
   * itineraries list and calls the tour service to resume it. This allows
   * users to return to tour sections they may have skipped earlier.
   *
   * @param skippedItinerary - The itinerary to resume
   *
   * @example
   * ```typescript
   * // Called from template when user clicks to resume a skipped itinerary
   * resumeSkippedItinerary(itinerary);
   * // The tour will return to the beginning of the specified itinerary
   * ```
   */
  resumeSkippedItinerary(skippedItinerary: TourItinerary) {
    const index = this.tourService
      .itineraries()
      .findIndex((itinerary) => itinerary.name === skippedItinerary.name);

    if (index > -1) {
      this.tourService.resumeSkippedItinerary(index);
    }
  }
}
